import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Mail, MapPin, ArrowRight, Building, FileText, Scale } from "lucide-react";
import { Link } from "react-router-dom";

const LegalDisclosurePage = () => {
  const pageRef = useRef<HTMLDivElement>(null);

  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: pageRef,
    offset: ["start start", "end end"]
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.9]);

  return (
    <div ref={pageRef}>
      <Helmet>
        <title>Legal Disclosure | Verdea</title>
        <meta
          name="description"
          content="Legal information and company details about Verdea."
        />
      </Helmet>

      <NavBar />

      <main className="relative pt-24 pb-16 min-h-screen overflow-hidden">
        {/* Background Elements - Green theme */}
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          {/* Primary gradient blur - Dark green theme */}
          <div
            className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
            style={{
              background: 'radial-gradient(circle at center, rgba(24, 76, 6, 0.3) 0%, rgba(24, 76, 6, 0) 70%)', // Dark green #184c06
              transform: 'translate(20%, -20%)'
            }}
          />

          {/* Secondary gradient blur */}
          <div
            className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
            style={{
              background: 'radial-gradient(circle at center, rgba(34, 147, 2, 0.3) 0%, rgba(34, 147, 2, 0) 70%)', // Light green #229302
              transform: 'translate(-20%, 20%)'
            }}
          />

          {/* Subtle dot grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: 'radial-gradient(rgba(34, 147, 2, 0.2) 1px, transparent 1px)', // Light green #229302
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            style={{ opacity: headerOpacity }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            <Badge variant="outline" className="px-4 py-1.5 mb-6 bg-green-50 border-green-100 text-green-800 text-sm font-medium dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400">
              Legal Notice
            </Badge>
            <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
              Legal <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent font-semibold">Disclosure</span>
            </h1>
          </motion.div>

          {/* Legal Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-4xl mx-auto"
          >
            <Card className="p-8 mb-8 bg-white/80 backdrop-blur-sm dark:bg-gray-800/80">
              <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-400">
                <p className="mb-6 text-lg leading-relaxed">
                  According to Austrian law, we are required to provide the following information about our company.
                </p>
              </div>
            </Card>
            
            {/* Company Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-12"
            >
              <Card className="p-8 bg-white/90 dark:bg-gray-800/90">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-[#229302] dark:text-green-400 mr-4">
                    <Building className="h-6 w-6" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Company Information</h2>
                </div>

                <div className="space-y-4 pl-16">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-6">
                      <div>
                        <p className="text-sm text-[#184c06] dark:text-green-400 font-medium mb-2">Company Name</p>
                        <p className="text-lg text-gray-900 dark:text-gray-100 font-medium">Greenblock GmbH</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-[#184c06] dark:text-green-400 font-medium mb-2">Registration Number</p>
                        <p className="text-lg text-gray-900 dark:text-gray-100 font-medium">596627v</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-[#184c06] dark:text-green-400 font-medium mb-2">Registration Court</p>
                        <p className="text-lg text-gray-900 dark:text-gray-100 font-medium">Handelsgericht Wien</p>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <div>
                        <p className="text-sm text-[#184c06] dark:text-green-400 font-medium mb-2">Managing Directors</p>
                        <p className="text-lg text-gray-900 dark:text-gray-100 font-medium">Sebastian Reich, (Shane Devane)</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-[#184c06] dark:text-green-400 font-medium mb-2">Address</p>
                        <div className="flex items-start">
                          <MapPin className="h-5 w-5 text-[#229302] mt-1 mr-2 flex-shrink-0" />
                          <p className="text-lg text-gray-900 dark:text-gray-100 font-medium">Klampfelberggasse 23, 1170 Wien</p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-[#184c06] dark:text-green-400 font-medium mb-2">Contact</p>
                        <div className="flex items-start">
                          <Mail className="h-5 w-5 text-[#229302] mt-1 mr-2 flex-shrink-0" />
                          <p className="text-lg text-gray-900 dark:text-gray-100 font-medium">
                            <a href="mailto:contact@verdea.io" className="hover:text-[#229302] transition-colors">
                              contact@verdea.io
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Legal Responsibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mb-12"
            >
              <Card className="p-8 bg-white/90 dark:bg-gray-800/90">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-[#229302] dark:text-green-400 mr-4">
                    <Scale className="h-6 w-6" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Legal Responsibility</h2>
                </div>

                <div className="space-y-4 pl-16">
                  <p className="text-gray-600 dark:text-gray-400">
                    In accordance with § 5 E-Commerce Act (ECG), § 14 Corporate Code (UGB), and § 63 Trade Regulation Act (GewO), we provide this mandatory disclosure of our company information.
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    The content of this website has been created with the utmost care. However, we cannot guarantee the accuracy, completeness, and timeliness of the information provided. We are not liable for damages or losses resulting from the use of this website's content.
                  </p>
                </div>
              </Card>
            </motion.div>

            {/* EU Dispute Resolution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-12"
            >
              <Card className="p-8 bg-white/90 dark:bg-gray-800/90">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-[#229302] dark:text-green-400 mr-4">
                    <FileText className="h-6 w-6" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">EU Dispute Resolution</h2>
                </div>

                <div className="space-y-4 pl-16">
                  <p className="text-gray-600 dark:text-gray-400">
                    The European Commission provides a platform for online dispute resolution (OS): <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#229302] hover:underline">https://ec.europa.eu/consumers/odr/</a>
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Our email address can be found in the company information above. We are neither obligated nor willing to participate in dispute resolution proceedings before a consumer arbitration board.
                  </p>
                </div>
              </Card>
            </motion.div>

            {/* Call to Action */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="relative mb-16 z-10 max-w-4xl mx-auto text-center"
            >
              <Card className="p-8 border-green-200 bg-gradient-to-br from-green-50 to-white dark:from-gray-800 dark:to-gray-900 dark:border-green-900/20">
                <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Need More Information?</h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
                  If you have any questions regarding our company or legal details, our team is ready to assist you.
                </p>
                <Link to="/contact" className="inline-block mx-auto">
                  <Button variant="default" size="lg" className="bg-[#184c06] hover:bg-[#229302] text-white w-auto px-6">
                    Contact us <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalDisclosurePage;